import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Alert, AlertDescription } from './ui/alert'
import { 
  User, 
  Mail, 
  Calendar, 
  Save, 
  Loader2, 
  CheckCircle,
  ArrowLeft,
  CreditCard,
  AlertTriangle,
  TrendingUp
} from 'lucide-react'
import { useAuth } from '../lib/auth-context'
import Navigation from './Navigation'

const ProfilePage = () => {
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    bio: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [hasChanges, setHasChanges] = useState(false)
  const [originalProfile, setOriginalProfile] = useState({})
  const [isPortalLoading, setIsPortalLoading] = useState(false)
  const [portalError, setPortalError] = useState('')
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false)
  const [upgradeError, setUpgradeError] = useState('')
  
  const { user, subscription, updateUser, loading: authLoading, refreshSubscription } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) {
      const userProfile = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        title: user.title || '',
        bio: user.bio || ''
      }
      setProfile(userProfile)
      setOriginalProfile(userProfile)
    }
  }, [user])

  // Refresh subscription data if user exists but subscription data is missing
  useEffect(() => {
    // Only refresh if auth is done loading, user exists, but no subscription data
    if (!authLoading && user && !subscription && !user.subscriptionStatus && user.stripeCustomerId) {
      // Small delay to allow auth context to finish, then refresh if needed
      const timer = setTimeout(() => {
        // Double-check conditions before refreshing
        if (!subscription && !user.subscriptionStatus) {
          refreshSubscription()
        }
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [authLoading, user, subscription, refreshSubscription])

  // Check for upgrade success/cancel messages in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const upgradeStatus = urlParams.get('upgrade')
    
    if (upgradeStatus === 'success') {
      setMessage({ type: 'success', text: 'Successfully upgraded to yearly billing! Your subscription has been updated.' })
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
    } else if (upgradeStatus === 'cancelled') {
      setMessage({ type: '', text: '' }) // Clear any existing messages
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setProfile(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Check if there are any changes
    const newProfile = { ...profile, [name]: value }
    const hasChanges = JSON.stringify(newProfile) !== JSON.stringify(originalProfile)
    setHasChanges(hasChanges)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    setMessage({ type: '', text: '' })

    try {
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/users/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profile)
      })

      if (response.ok) {
        const updatedUser = await response.json()
        setMessage({ type: 'success', text: 'Profile updated successfully!' })
        setOriginalProfile(profile)
        setHasChanges(false)
        
        // Update the user context if updateUser function exists
        if (updateUser) {
          updateUser(updatedUser)
        }
      } else {
        const error = await response.json()
        setMessage({ type: 'error', text: error.message || 'Failed to update profile' })
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setProfile(originalProfile)
    setHasChanges(false)
    setMessage({ type: '', text: '' })
  }

  const handleManageSubscription = async () => {
    setIsPortalLoading(true)
    setPortalError('')

    try {
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/users/customer-portal`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (response.ok && data.success && data.url) {
        // Redirect to Stripe customer portal
        window.location.href = data.url
      } else {
        // Handle errors from backend
        const errorMessage = data.error || data.message || 'Failed to create portal session. Please try again.'
        setPortalError(errorMessage)
        
        // If no Stripe customer ID, show helpful message
        if (data.error && data.error.includes('No Stripe customer ID')) {
          setPortalError('No subscription found. Please contact support if you believe this is an error.')
        }
      }
    } catch (error) {
      console.error('Error creating customer portal session:', error)
      setPortalError('Network error. Please check your connection and try again.')
    } finally {
      setIsPortalLoading(false)
    }
  }

  const handleCancelSubscription = () => {
    // Open cancellation page with embedded Typeform
    // Uses the page URL where Typeform is embedded (better UX - keeps users on your domain)
    const cancelPageUrl = import.meta.env.VITE_CANCEL_MEMBERSHIP_URL || 'https://impactacceleratorcircle.com/cancel-membership'
    
    // Open cancellation page in new window/tab
    window.open(cancelPageUrl, '_blank', 'noopener,noreferrer')
  }


  // Check if user has active or trialing subscription
  const hasActiveSubscription = () => {
    // Don't show buttons while auth is still loading
    if (authLoading) {
      return false
    }
    
    // Check subscription from auth context (separate state)
    const status = subscription?.status || user?.subscriptionStatus
    
    // If we have a status, check if it's active or trialing
    if (status === 'active' || status === 'trialing') {
      return true
    }
    
    // Fallback: If user has a stripe_customer_id, they likely have a subscription
    // This helps in cases where subscription data hasn't loaded yet
    // We'll check this as a secondary indicator
    if (!status && user?.stripeCustomerId) {
      // If we have a customer ID but no subscription status, try to fetch it
      // For now, return true to show buttons - they'll get an error if no subscription exists
      return true
    }
    
    return false
  }

  // Check if user is on monthly plan (to show yearly upsell)
  const isMonthlyPlan = () => {
    // We'll check this by looking at the subscription price ID
    // For now, show upsell if they have an active subscription
    // You can refine this later by checking the actual price ID
    return hasActiveSubscription()
  }

  const handleUpgradeToYearly = async () => {
    setIsUpgradeLoading(true)
    setUpgradeError('')

    try {
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/users/upgrade-to-yearly`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (response.ok && data.success && data.url) {
        // Redirect to Stripe checkout for yearly upgrade
        window.location.href = data.url
      } else {
        const errorMessage = data.error || data.message || 'Failed to create upgrade session. Please try again.'
        setUpgradeError(errorMessage)
      }
    } catch (error) {
      console.error('Error creating yearly upgrade session:', error)
      setUpgradeError('Network error. Please check your connection and try again.')
    } finally {
      setIsUpgradeLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
            <p className="text-gray-600 font-medium">Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="rounded-xl hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Profile Settings</h1>
              <p className="text-lg text-gray-600">Manage your account information and preferences</p>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="max-w-4xl mx-auto">
          <Card className="rounded-3xl shadow-lg border-0 bg-white">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Personal Information</CardTitle>
              <CardDescription className="text-gray-600">
                Update your profile details and contact information
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {message.text && (
                  <Alert 
                    variant={message.type === 'success' ? 'default' : 'destructive'} 
                    className={`rounded-2xl ${
                      message.type === 'success' 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      {message.type === 'success' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <div className="w-4 h-4 bg-red-600 rounded-full" />
                      )}
                      <AlertDescription 
                        className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}
                      >
                        {message.text}
                      </AlertDescription>
                    </div>
                  </Alert>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700">
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={profile.firstName}
                      onChange={handleInputChange}
                      className="rounded-2xl h-12"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700">
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={profile.lastName}
                      onChange={handleInputChange}
                      className="rounded-2xl h-12"
                      required
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={profile.email}
                      onChange={handleInputChange}
                      className="rounded-2xl h-12"
                      required
                      disabled
                    />
                    <p className="text-xs text-gray-500">Email cannot be changed</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={profile.phone}
                      onChange={handleInputChange}
                      className="rounded-2xl h-12"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* Professional Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-sm font-semibold text-gray-700">
                      Company
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={profile.company}
                      onChange={handleInputChange}
                      className="rounded-2xl h-12"
                      placeholder="Your company name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="title" className="text-sm font-semibold text-gray-700">
                      Job Title
                    </Label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      value={profile.title}
                      onChange={handleInputChange}
                      className="rounded-2xl h-12"
                      placeholder="Your job title"
                    />
                  </div>
                </div>

                {/* Bio */}
                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-sm font-semibold text-gray-700">
                    Bio
                  </Label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={profile.bio}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                    placeholder="Tell us about yourself..."
                  />
                </div>

                {/* Account Information */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gray-100 rounded-xl flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Account Information</h3>
                      <p className="text-sm text-gray-600">Your account details</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-600">Member Since</Label>
                      <p className="text-sm text-gray-900 font-medium">
                        {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 'Recently'}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-600">Account Status</Label>
                      <p className="text-sm text-gray-900 font-medium">
                        {user?.subscription?.status === 'active' ? 'Active Member' : 'Standard Account'}
                      </p>
                    </div>
                  </div>

                  {/* Yearly Upgrade Upsell Section */}
                  {isMonthlyPlan() && (
                    <div className="mt-6 pt-6 border-t border-green-200">
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-green-900 mb-1">Save $2,000 with Annual Billing</h4>
                            <p className="text-sm text-green-700 mb-4">
                              Switch to yearly billing and save $2,000 per year. Your subscription will be updated immediately.
                            </p>
                            {upgradeError && (
                              <Alert variant="destructive" className="mb-4 rounded-2xl border-red-200 bg-red-50">
                                <AlertDescription className="text-red-700 text-sm">
                                  {upgradeError}
                                </AlertDescription>
                              </Alert>
                            )}
                            <Button
                              type="button"
                              onClick={handleUpgradeToYearly}
                              disabled={isUpgradeLoading}
                              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                            >
                              {isUpgradeLoading ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <TrendingUp className="mr-2 h-4 w-4" />
                                  Upgrade to Yearly
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Manage Subscription Section */}
                  {hasActiveSubscription() && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <CreditCard className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">Manage Subscription</h4>
                          <p className="text-sm text-gray-600 mb-4">
                            See Invoices and update your billing information
                          </p>
                          {portalError && (
                            <Alert variant="destructive" className="mb-4 rounded-2xl border-red-200 bg-red-50">
                              <AlertDescription className="text-red-700 text-sm">
                                {portalError}
                              </AlertDescription>
                            </Alert>
                          )}
                          <Button
                            type="button"
                            onClick={handleManageSubscription}
                            disabled={isPortalLoading}
                            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                          >
                            {isPortalLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <CreditCard className="mr-2 h-4 w-4" />
                                Manage Subscription
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Danger Zone Section */}
                {hasActiveSubscription() && (
                  <div className="pt-6 border-t border-red-200">
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-red-900 mb-1">Cancel & Deactivate Account</h4>
                          <p className="text-sm text-red-700 mb-4">
                            Canceling your subscription will end your access to all premium features. You can reactivate at any time.
                          </p>
                          <Button
                            type="button"
                            onClick={handleCancelSubscription}
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                          >
                            <AlertTriangle className="mr-2 h-4 w-4" />
                            Cancel & Deactivate Account
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <Button
                    type="submit"
                    disabled={!hasChanges || isSaving}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  
                  {hasChanges && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      className="flex-1 h-12 rounded-2xl border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
